import * as React from 'react';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import type { DateRange, Matcher } from 'react-day-picker';
import { buildDateRangeMatchers, parseDateStr, toDateStr } from '../lib/date-string.js';
import { Label } from './label.js';
import {
  DatePickerPopoverContent,
  DatePickerTrigger,
  useCalendarPopoverSide,
  useNarrowCalendarViewport,
} from './date-picker-trigger.js';
import { Popover } from './popover.js';
import { Calendar } from './calendar.js';

/** Empty strings render as `undefined` (attribute omitted) rather than `""`,
 * kept as a standalone helper so its `||` doesn't count towards
 * `DateRangeButtonField`'s own complexity. */
function toDataAttr(value: string): string | undefined {
  return value || undefined;
}

/** Formats the trigger's "from -> to" label, kept separate so its two
 * ternaries don't count towards `DateRangeButtonField`'s own complexity. */
function RangeTriggerLabel({
  fromDate,
  toDate,
  placeholder,
  formatDate,
}: {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  placeholder: string;
  formatDate: (date: Date) => string;
}) {
  if (!fromDate) {
    return <span className="text-muted-foreground truncate">{placeholder}</span>;
  }
  return (
    <span className="truncate">
      {formatDate(fromDate)}
      {' \u2192 '}
      {toDate ? formatDate(toDate) : '...'}
    </span>
  );
}

export interface DateRangeButtonFieldProps {
  /** From date as a YYYY-MM-DD string, or '' when unset. */
  fromValue: string;
  /** To date as a YYYY-MM-DD string, or '' when unset. */
  toValue: string;
  onChange: (next: { from: string; to: string }) => void;
  /** Pass a translated string. Rendered above the trigger when provided. */
  label?: string;
  /** Shown in the trigger when no range is selected. Pass a translated string. */
  placeholder?: string;
  /** YYYY-MM-DD string. Dates before this are disabled/unselectable in the Calendar. */
  minDate?: string;
  /** YYYY-MM-DD string. Dates after this are disabled/unselectable in the Calendar. */
  maxDate?: string;
  /** Months shown side by side on desktop. Defaults to 2; narrow viewports show one. */
  numberOfMonths?: number;
  disabled?: boolean;
  /** Forwarded to the trigger Button so a `<Label htmlFor>` can target it. */
  id?: string;
  /**
   * Applied to the single trigger Button. The trigger also carries the
   * current `fromValue`/`toValue` as `data-from-value`/`data-to-value`
   * attributes, so tests can assert the selected range without parsing the
   * locale-formatted trigger label text.
   */
  testId?: string;
  /** date-fns format pattern used to render the selected dates. Defaults to 'd MMM yyyy'. */
  formatPattern?: string;
  locale?: Locale;
  className?: string;
}

/**
 * Form-field style date RANGE picker with a single trigger and a single
 * calendar (Airbnb/Google-Flights style). One `Button` opens one `Popover`
 * containing a `Calendar mode="range"`; the first click sets "from", the
 * popover stays open, and the second click (on or after "from") sets "to"
 * and closes it. Clicking a date before the pending "from" restarts the
 * range at that date instead of completing it. Same overall interaction as
 * `DateRangePill`, just styled as a full-width labeled form field instead
 * of a compact filter-bar pill.
 *
 * The sanctioned primitive for date-range fields in forms/dialogs
 * (availability windows, reservation edits, campaign duration) — replaces
 * pairs of native `<input type="date">`.
 */
export function DateRangeButtonField({
  fromValue,
  toValue,
  onChange,
  label,
  placeholder = 'Select date range',
  minDate,
  maxDate,
  numberOfMonths = 2,
  disabled,
  id,
  testId,
  formatPattern = 'd MMM yyyy',
  locale,
  className,
}: DateRangeButtonFieldProps) {
  const [open, setOpen] = React.useState(false);
  const isNarrowViewport = useNarrowCalendarViewport();
  const fromDate = parseDateStr(fromValue);
  const toDate = parseDateStr(toValue);
  const isOpen = open && !disabled;
  const { triggerRef, contentRef, side, align } = useCalendarPopoverSide();

  const dateRange: DateRange | undefined =
    fromDate || toDate ? { from: fromDate, to: toDate } : undefined;

  const [displayMonth, setDisplayMonth] = React.useState(() => fromDate ?? new Date());

  // Re-anchor the displayed month to the current "from" date every time the
  // popover opens (falling back to today when nothing is picked yet), but
  // never while it's already open — that would fight the user's own month
  // navigation. Same anchoring idea as `DatePickerButton`'s `anchorDate`.
  React.useEffect(() => {
    if (!isOpen) return;
    setDisplayMonth(fromDate ?? new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only resync on the closed->open transition, not on every `fromDate` change
  }, [isOpen]);

  const disabledMatchers = React.useMemo<Matcher[] | undefined>(() => {
    const matchers = buildDateRangeMatchers(minDate, maxDate);
    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  // Driven by react-day-picker's `triggerDate` (the actually-clicked day)
  // rather than its computed `range`. With `mode="range"` and no `min` set,
  // react-day-picker's own range math (`addToRange`) collapses an empty
  // range straight to `{ from: clicked, to: clicked }` on the very FIRST
  // click, which made the old `if (from && to) close()` check fire
  // immediately every time. Tracking our own from/to state instead makes
  // the two-click flow explicit and matches `DateRangePill`'s pattern of
  // deriving `from`/`to` from the callback rather than trusting react-day-picker
  // to already know when a range is "done".
  function handleSelect(_range: DateRange | undefined, clickedDate: Date) {
    const clickedStr = toDateStr(clickedDate);
    const hasPendingFrom = Boolean(fromDate) && !toDate;
    const isOnOrAfterFrom = hasPendingFrom && fromDate !== undefined && clickedDate >= fromDate;

    if (isOnOrAfterFrom) {
      // Second click, on or after the pending "from": completes the range.
      onChange({ from: fromValue, to: clickedStr });
      setOpen(false);
      return;
    }

    // First click, restarting after a complete range, or picking a date
    // earlier than the pending "from": (re)start the range at this date and
    // keep the popover open so the user can pick the other end.
    onChange({ from: clickedStr, to: '' });
  }

  const formatDate = (date: Date) => format(date, formatPattern, locale ? { locale } : undefined);

  return (
    <div className={className}>
      {label ? (
        <Label className="mb-1.5 block" htmlFor={id}>
          {label}
        </Label>
      ) : null}
      <Popover open={isOpen} onOpenChange={(next) => setOpen(next && !disabled)}>
        <DatePickerTrigger
          ref={triggerRef}
          disabled={disabled}
          id={id}
          testId={testId}
          data-from-value={toDataAttr(fromValue)}
          data-to-value={toDataAttr(toValue)}
        >
          <RangeTriggerLabel
            fromDate={fromDate}
            toDate={toDate}
            placeholder={placeholder}
            formatDate={formatDate}
          />
        </DatePickerTrigger>
        <DatePickerPopoverContent ref={contentRef} side={side} align={align}>
          <Calendar
            mode="range"
            month={displayMonth}
            onMonthChange={setDisplayMonth}
            selected={dateRange}
            onSelect={handleSelect}
            numberOfMonths={isNarrowViewport ? 1 : numberOfMonths}
            disabled={disabledMatchers}
            {...(locale ? { locale } : {})}
          />
        </DatePickerPopoverContent>
      </Popover>
    </div>
  );
}
